import { useState } from "react";
import { useSelector } from "react-redux";
import { RefreshCw, Presentation } from "lucide-react";
import { Alert, Skeleton } from "@mui/material";
import { useHomeSummary } from "../../hooks/useHomeSummary";
import { SummaryStat } from "./summary/SummaryPanel";
import { ProjectedProfitStat } from "./summary/ProjectedProfitStat";
import { CommercialSummary } from "./summary/CommercialSummary";
import { ProjectsSummary } from "./summary/ProjectsSummary";
import { CashSummary } from "./summary/CashSummary";
import { TasksSummary } from "./summary/TasksSummary";
import { DialogFormTask } from "../tasks/DialogFormTask";
import { summaryAmount, summaryDate, solesAmount } from "./summary/formatters";
import "./weeklyHome.css";

export const Home = () => {
  const { name, uid, idpro } = useSelector((state) => state.auth);
  const [meeting, setMeeting] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const { data, loading, error, refresh } = useHomeSummary(
    "",
    `${uid}-${idpro}`,
  );
  const currency = data?.currencies.find(
    (item) => String(item.id) === data.currencyId,
  );
  const sections = data?.sections;
  const projects = sections?.projects.data;
  const budgets = sections?.budgets.data;
  const cash = sections?.cash.data;
  const visible =
    sections &&
    Object.values(sections).some((section) => section.status !== "restricted");

  return (
    <div className={`home-week ${meeting ? "home-week--meeting" : ""}`}>
      <header className="home-week-header">
        <div>
          {/* <span className="home-week-eyebrow">INICIO</span> */}
          <h1>Tu negocio, esta semana</h1>
          <p>
            Hola, <span className="home-week-name">{name}</span>. Revisa dónde
            estamos y organiza los próximos pasos.
          </p>
        </div>
        <div className="home-week-actions">
          <button
            type="button"
            className="home-week-button"
            onClick={refresh}
            disabled={loading}
          >
            <RefreshCw size={16} aria-hidden="true" />
            Actualizar
          </button>
          <button
            type="button"
            className="home-week-button"
            aria-pressed={meeting}
            onClick={() => setMeeting((value) => !value)}
          >
            <Presentation size={16} aria-hidden="true" />
            {meeting ? "Salir de reunión" : "Modo reunión"}
          </button>
        </div>
      </header>
      {error && (
        <Alert
          severity="error"
          action={
            <button className="home-week-button" onClick={refresh}>
              Reintentar
            </button>
          }
        >
          {error}
        </Alert>
      )}
      {loading && (
        <div
          aria-label="Cargando resumen"
          role="status"
          className="home-week-loading"
        >
          <Skeleton height={50} />
          <div className="home-week-stats">
            {[1, 2, 3, 4].map((key) => (
              <Skeleton key={key} variant="rounded" height={125} />
            ))}
          </div>
          <Skeleton variant="rounded" height={250} />
        </div>
      )}
      {!loading && data && (
        <>
          <div className="home-week-period">
            <div>
              <h2>
                Semana del {summaryDate(data.period.weekStart)} al{" "}
                {summaryDate(data.period.weekEnd, "D MMM YYYY")}
              </h2>
              <p>
                Actualizado {summaryDate(data.period.asOf, "D MMM, HH:mm")} ·
                Hora de Lima
              </p>
            </div>
          </div>
          {meeting && (
            <nav className="home-week-agenda" aria-label="Agenda de la reunión">
              {sections.opportunities.status !== "restricted" && (
                <a href="#home-opportunities">1. Nuevos negocios</a>
              )}
              {sections.budgets.status !== "restricted" && (
                <a href="#home-budgets">2. Cotizaciones</a>
              )}
              {sections.projects.status !== "restricted" && (
                <a href="#home-projects">3. Proyectos</a>
              )}
              {sections.cash.status !== "restricted" && (
                <a href="#home-cash">4. Dinero</a>
              )}
              {sections.tasks.status !== "restricted" && (
                <a href="#home-tasks">5. Acuerdos</a>
              )}
            </nav>
          )}
          <div className="home-week-stats">
            {sections.budgets.status !== "restricted" && (
              <SummaryStat
                label="Presupuestos por aprobar"
                value={solesAmount(budgets?.amount)}
                context={
                  budgets
                    ? `${budgets.count} pendientes de aprobación · en soles`
                    : "Datos no disponibles"
                }
                href="/ppto?from=inicio"
              />
            )}
            {sections.projects.status !== "restricted" && (
              <SummaryStat
                label="Proyectos en proceso"
                value={projects?.count ?? "—"}
                context={
                  projects
                    ? `${projects.attention} necesitan atención`
                    : "Datos no disponibles"
                }
                href="/projects"
              />
            )}
            {data.permissions.costs &&
              sections.projects.status !== "restricted" && (
                <ProjectedProfitStat projects={projects} currency={currency} />
              )}
            {sections.cash.status !== "restricted" && (
              <SummaryStat
                label="Saldo proyectado de la semana"
                value={summaryAmount(cash?.projected_balance, currency)}
                context={
                  sections.cash.status === "error"
                    ? "Datos no disponibles"
                    : cash?.projected_balance != null
                      ? `Ingresos netos − egresos totales hasta el ${summaryDate(data.period.weekEnd)}`
                      : "Datos no disponibles"
                }
                href="/flows"
                description="Ingresos proyectados netos menos egresos proyectados totales de hoy al domingo, de los proyectos aprobados y en proceso. No incluye bancos ni saldos de facturas o compras."
              />
            )}
          </div>
          {!visible && (
            <Alert severity="info">
              Tu usuario no tiene acceso a los módulos de este resumen. Solicita
              los accesos correspondientes al administrador.
            </Alert>
          )}
          <div className="home-week-grid">
            <CommercialSummary
              sections={sections}
              currency={currency}
              meeting={meeting}
            />
            <ProjectsSummary
              section={sections.projects}
              currency={currency}
              permissions={data.permissions}
            />
            <CashSummary
              section={sections.cash}
              currency={currency}
              period={data.period}
              meeting={meeting}
            />
            <TasksSummary
              section={sections.tasks}
              today={data.period.today}
              onEdit={setSelectedTask}
            />
          </div>
          <footer className="home-week-footer">
            Situación actual según tus permisos. Los presupuestos se consolidan en soles; los demás importes se muestran en {currency?.nombre}.
            {data.permissions.costs &&
              data.permissions.projects &&
              " La utilidad utiliza la proyección de costos registrada en cada proyecto."}
            {data.permissions.tasks &&
              " Las tareas incluyen proyectos accesibles y tus tareas generales."}
          </footer>
        </>
      )}
      {selectedTask !== null && (
        <DialogFormTask
          key={selectedTask}
          open
          setOpen={(open) => {
            if (!open) {
              setSelectedTask(null);
              refresh();
            }
          }}
          taskId={selectedTask}
          refreshObj={{ fn_: refresh, filters_: {} }}
        />
      )}
    </div>
  );
};
